import { EditorNode } from "../../core";
import {
  convertPNodeToLi,
  detectListAction,
  convertNestedList,
} from "./orderedList.helper";

export class orderedListCommand {
  constructor(editor) {
    this.editor = editor;
  }

  execute() {
    const selection = this.editor.selection;
    const selectedBlocks = selection.getSelectedBlocks();

    const filterBlocks = selectedBlocks.filter((block) => {
      // Filter out ul/ol elements - they are not valid blocks for list operations
      const tagName = block.tagName.toLowerCase();
      if (tagName === "ul" || tagName === "ol") {
        return false;
      }

      const parentOuter = EditorNode.getParentOuter(block, this.editor.root);

      // Nếu không có parent outer hoặc parent outer không phải blockquote
      const isBlockquote = parentOuter?.tagName.toLowerCase() === "blockquote";
      const shouldKeep = !isBlockquote;

      return shouldKeep;
    });

    // If no valid blocks, return early
    if (filterBlocks.length === 0) {
      return;
    }

    const checked = detectListAction({ blocks: filterBlocks });

    // Save selection trước khi modify DOM
    const savedSelection = selection.save();

    switch (checked) {
      case "create": {
        const createdList = this.createList({ blocks: filterBlocks });

        // Restore selection to all li in created list
        if (createdList) {
          const sel = window.getSelection();
          if (sel) {
            try {
              const range = document.createRange();

              const findFirstTextNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) return node;
                for (let i = 0; i < node.childNodes.length; i++) {
                  const result = findFirstTextNode(node.childNodes[i]);
                  if (result) return result;
                }
                return null;
              };

              const findLastTextNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) return node;
                for (let i = node.childNodes.length - 1; i >= 0; i--) {
                  const result = findLastTextNode(node.childNodes[i]);
                  if (result) return result;
                }
                return null;
              };

              const firstTextNode = findFirstTextNode(createdList);
              const lastTextNode = findLastTextNode(createdList);

              if (firstTextNode && lastTextNode) {
                range.setStart(firstTextNode, 0);
                range.setEnd(
                  lastTextNode,
                  lastTextNode.textContent?.length || 0,
                );
                sel.removeAllRanges();
                sel.addRange(range);
              }
            } catch (e) {
              return e
            }
          }
        }
        return;
      }
      case "remove": {
        this.removeList({ blocks: filterBlocks });
        return; // Early return to skip normal selection restore
      }
      case "split": {
        this.splitList({ blocks: filterBlocks });
        break;
      }
      case "merge": {
        this.mergeList({ blocks: filterBlocks });

        // Restore selection manually after merge
        this.restoreSelectionForBlocks(filterBlocks);
        return;
      }
      case "revert": {
        this.toggleList({ blocks: filterBlocks });
        break;
      }
      case "convertNested": {
        // Don't save/restore selection here - let convertNestedList handle it
        convertNestedList({ blocks: filterBlocks, targetType: "ol" });

        // Manually restore selection to highlight all converted blocks
        if (filterBlocks.length > 0) {
          const sel = window.getSelection();
          if (sel) {
            try {
              const range = document.createRange();

              // Find first and last text nodes
              const findFirstTextNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) return node;
                for (let i = 0; i < node.childNodes.length; i++) {
                  const result = findFirstTextNode(node.childNodes[i]);
                  if (result) return result;
                }
                return null;
              };

              const findLastTextNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) return node;
                for (let i = node.childNodes.length - 1; i >= 0; i--) {
                  const result = findLastTextNode(node.childNodes[i]);
                  if (result) return result;
                }
                return null;
              };

              const firstBlock = filterBlocks[0];
              const lastBlock = filterBlocks[filterBlocks.length - 1];

              const firstTextNode = findFirstTextNode(firstBlock);
              const lastTextNode = findLastTextNode(lastBlock);

              if (firstTextNode && lastTextNode) {
                range.setStart(firstTextNode, 0);
                range.setEnd(
                  lastTextNode,
                  lastTextNode.textContent?.length || 0,
                );
                sel.removeAllRanges();
                sel.addRange(range);
              }

              requestAnimationFrame(() => {
                this.editor.root
                  .querySelectorAll('li[data-from-command="true"]')
                  .forEach(li => li.removeAttribute("data-from-command"));
              });
            } catch (e) {
              return e
            }
          }
        }

        return; // Early return to skip normal selection restore
      }
      default:
        return;
    }

    // Restore selection sau khi modify DOM
    selection.restore(savedSelection);
  }

  getParentLi(node) {
    while (node && node !== this.editor.root) {
      if (node.tagName?.toLowerCase() === "li") return node;
      node = node.parentElement;
    }
    return null;
  }

  restoreSelectionForBlocks(originalBlocks) {
    // After DOM changes, we need to find the new nodes
    // For create/merge, the blocks are now inside <li> elements
    const sel = window.getSelection();
    if (!sel || originalBlocks.length === 0) return;

    try {
      const range = document.createRange();

      const findFirstTextNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return node;
        for (let i = 0; i < node.childNodes.length; i++) {
          const result = findFirstTextNode(node.childNodes[i]);
          if (result) return result;
        }
        return null;
      };

      const findLastTextNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return node;
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          const result = findLastTextNode(node.childNodes[i]);
          if (result) return result;
        }
        return null;
      };

      // Try to find the list that contains our blocks
      const firstBlock = originalBlocks[0];
      const lastBlock = originalBlocks[originalBlocks.length - 1];

      // Check if blocks are now in list
      const firstLi = this.getParentLi(firstBlock);
      const lastLi = this.getParentLi(lastBlock);

      if (firstLi && lastLi) {
        // Blocks are in list, select from first li to last li
        const firstTextNode = findFirstTextNode(firstLi);
        const lastTextNode = findLastTextNode(lastLi);

        if (firstTextNode && lastTextNode) {
          range.setStart(firstTextNode, 0);
          range.setEnd(lastTextNode, lastTextNode.textContent?.length || 0);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } else {
        // Blocks are not in list, select original blocks
        const firstTextNode = findFirstTextNode(firstBlock);
        const lastTextNode = findLastTextNode(lastBlock);

        if (firstTextNode && lastTextNode) {
          range.setStart(firstTextNode, 0);
          range.setEnd(lastTextNode, lastTextNode.textContent?.length || 0);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    } catch (e) {
      return e
    }
  }

  createList = ({ blocks }) => {
    if (blocks.length === 0) return;

    const firstBlock = blocks[0];
    const parent = firstBlock.parentNode;

    if (!parent) return;

    // Create ol element
    const ulNode = document.createElement("ol");

    // Insert ol at the position of first block
    parent.insertBefore(ulNode, firstBlock);

    // Convert blocks to li elements
    const result = convertPNodeToLi({ blocks });

    // Append all li to ol
    result.forEach((li) => ulNode.appendChild(li));

    // Return the created list for selection restore
    return ulNode;
  };

  removeList = ({ blocks }) => {
    if (!blocks.length) return;

    const processedLists = new Set();
    const createdParagraphs = [];

    blocks.forEach((block) => {
      const li = this.getParentLi(block);
      if (!li) return;

      const list = li.parentElement;
      if (!list) return;

      const tag = list.tagName.toLowerCase();
      if (tag !== "ol") return;

      // tránh xử lý 1 list nhiều lần
      if (processedLists.has(list)) return;
      processedLists.add(list);

      const parent = list.parentNode;
      if (!parent) return;

      const anchor = list.nextSibling;

      const fragment = document.createDocumentFragment();

      const listItems = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li",
      );

      listItems.forEach((li) => {
        const p = document.createElement("p");

        // move children sang p
        while (li.firstChild) {
          p.appendChild(li.firstChild);
        }

        fragment.appendChild(p);
        createdParagraphs.push(p);
      });

      parent.insertBefore(fragment, anchor);
      parent.removeChild(list);
    });

    // Restore selection to created paragraphs
    if (createdParagraphs.length > 0) {
      const sel = window.getSelection();
      if (sel) {
        try {
          const range = document.createRange();

          const findFirstTextNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) return node;
            for (let i = 0; i < node.childNodes.length; i++) {
              const result = findFirstTextNode(node.childNodes[i]);
              if (result) return result;
            }
            return null;
          };

          const findLastTextNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) return node;
            for (let i = node.childNodes.length - 1; i >= 0; i--) {
              const result = findLastTextNode(node.childNodes[i]);
              if (result) return result;
            }
            return null;
          };

          const firstParagraph = createdParagraphs[0];
          const lastParagraph = createdParagraphs[createdParagraphs.length - 1];

          const firstTextNode = findFirstTextNode(firstParagraph);
          const lastTextNode = findLastTextNode(lastParagraph);

          if (firstTextNode && lastTextNode) {
            range.setStart(firstTextNode, 0);
            range.setEnd(lastTextNode, lastTextNode.textContent?.length || 0);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } catch (e) {
          return e
        }
      }
    }
  };

  splitList = ({ blocks }) => {
    if (!blocks.length) return;

    const processedLists = new Set();

    blocks.forEach((block) => {
      const li = this.getParentLi(block);
      if (!li) return;

      const list = li.parentElement;
      if (!list) return;

      const tag = list.tagName.toLowerCase();
      if (tag !== "ol" && tag !== "ol") return;

      if (processedLists.has(list)) return;
      processedLists.add(list);

      const parent = list.parentNode;
      if (!parent) return;

      const listItems = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li",
      );

      const selectedLis = blocks
        .map((b) => this.getParentLi(b))
        .filter(Boolean);

      const before = [];
      const middle = [];
      const after = [];

      let mode = "before";

      listItems.forEach((item) => {
        if (selectedLis.includes(item)) {
          mode = "middle";
          middle.push(item);
          return;
        }

        if (mode === "before") before.push(item);
        else after.push(item);
      });

      const fragment = document.createDocumentFragment();

      // before list
      if (before.length) {
        const beforeList = document.createElement(tag);
        before.forEach((li) => beforeList.appendChild(li));
        fragment.appendChild(beforeList);
      }

      // middle -> p
      middle.forEach((li) => {
        const p = document.createElement("p");
        while (li.firstChild) {
          p.appendChild(li.firstChild);
        }
        fragment.appendChild(p);
      });

      // after list
      if (after.length) {
        const afterList = document.createElement(tag);
        after.forEach((li) => afterList.appendChild(li));
        fragment.appendChild(afterList);
      }

      parent.insertBefore(fragment, list);
      parent.removeChild(list);
    });
  };

  mergeList = ({ blocks }) => {
    if (!blocks.length) return;

    // Collect theo thứ tự xuất hiện trong blocks
    const seenLists = new Set();
    const listsInOrder = [];
    const paragraphs = [];

    blocks.forEach((block) => {
      const li = this.getParentLi(block);

      if (li) {
        // Block nằm trong list
        const list = li.parentElement;
        if (
          list &&
          (list.tagName.toLowerCase() === "ul" ||
            list.tagName.toLowerCase() === "ol") &&
          !seenLists.has(list)
        ) {
          seenLists.add(list);
          listsInOrder.push(list);
        }
      } else if (block.tagName.toLowerCase() === "p") {
        // Block là paragraph
        paragraphs.push(block);
      }
    });

    if (listsInOrder.length === 0 && paragraphs.length === 0) return;

    // Tạo 1 list mới để merge tất cả
    const mergedList = document.createElement("ol");

    // Tìm vị trí insert - phải là direct child của editor root
    let insertBefore = null;
    let insertParent = null;

    // Tìm first list hoặc paragraph để xác định vị trí
    if (listsInOrder.length > 0) {
      const firstList = listsInOrder[0];
      insertBefore = firstList;
      insertParent = firstList.parentNode;
    } else if (paragraphs.length > 0) {
      insertBefore = paragraphs[0];
      insertParent = paragraphs[0].parentNode;
    }

    if (!insertParent || insertParent !== this.editor.root) {
      return;
    }

    // Insert merged list
    insertParent.insertBefore(mergedList, insertBefore);

    // Merge theo đúng thứ tự xuất hiện trong blocks
    blocks.forEach((block) => {
      const li = this.getParentLi(block);

      if (li) {
        // Nếu là li, move nó vào merged list (nếu chưa được move)
        if (li.parentElement !== mergedList) {
          mergedList.appendChild(li);
        }
      } else if (block.tagName.toLowerCase() === "p") {
        // Nếu là p, convert thành li
        const newLi = document.createElement("li");
        while (block.firstChild) {
          newLi.appendChild(block.firstChild);
        }
        mergedList.appendChild(newLi);
        block.parentNode?.removeChild(block);
      }
    });

    // Remove các list rỗng
    listsInOrder.forEach((list) => {
      if (list.children.length === 0) {
        list.parentNode?.removeChild(list);
      }
    });
  };

  toggleList = ({ blocks }) => {

    if (!blocks.length) return;

    // Tìm thẻ ul parent
    const firstBlock = blocks[0];

    const li = this.getParentLi(firstBlock);

    if (!li) return;

    const ulList = li.parentElement;

    if (!ulList || ulList.tagName.toLowerCase() !== "ul") return;

    const parent = ulList.parentNode;
    if (!parent) return;

    // Tạo ol mới
    const olNode = document.createElement("ol");

    while (ulList.firstChild) {
      olNode.appendChild(ulList.firstChild);
    }

    // Replace ul bằng ol
    parent.insertBefore(olNode, ulList);
    parent.removeChild(ulList);
  };
}
